// Portal Layout Component - Base template for public enrollment pages
import React, { useState, useEffect } from 'react';
import { Facebook, Instagram } from 'lucide-react';
import pagesService from '../../services/pagesService';

const PortalLayout = ({
  children,
  teacherInfo,
  portalSettings = {},
  userId = null,
  currentPage = null,
  showHeader = true,
  showFooter = true
}) => {
  const [settings, setSettings] = useState({
    logo: null,
    logoIndent: 0,
    colorScheme: 'blue',
    businessName: '',
    tagline: '',
    contactEmail: '',
    contactPhone: '',
    address: ''
  });
  const [pages, setPages] = useState([]);

  // Update settings when portalSettings prop changes
  useEffect(() => {
    console.log('PortalLayout - portalSettings received:', portalSettings);
    console.log('PortalLayout - logoIndent value:', portalSettings.logoIndent);
    setSettings(prev => ({
      ...prev,
      ...portalSettings
    }));
  }, [portalSettings]);

  // Fetch published pages for navigation
  useEffect(() => {
    if (userId) {
      fetchPages();
    }
  }, [userId]);

  const fetchPages = async () => {
    try {
      console.log('PortalLayout - Fetching pages for userId:', userId);
      const pagesData = await pagesService.getPublicPages(userId);
      console.log('PortalLayout - Pages fetched:', pagesData);
      setPages(pagesData || []);
    } catch (error) {
      console.error('PortalLayout - Error fetching pages:', error);
    }
  };


  // Color scheme configurations
  const colorSchemes = {
    craft: {
      header: 'bg-craft-primary',
      headerText: 'text-craft-dark',
      primary: 'bg-craft-primary',
      primaryHover: 'hover:bg-craft-secondary',
      primaryText: 'text-craft-primary',
      primaryBg: 'bg-craft-secondary',
      secondary: 'bg-craft-tertiary',
      accent: 'bg-craft-accent',
      footer: 'bg-craft-dark',
      footerText: 'text-white'
    },
    summer: {
      header: 'bg-summer-primary',
      headerText: 'text-summer-dark',
      primary: 'bg-summer-primary',
      primaryHover: 'hover:bg-summer-secondary',
      primaryText: 'text-summer-primary',
      primaryBg: 'bg-summer-secondary',
      secondary: 'bg-summer-tertiary',
      accent: 'bg-summer-accent',
      footer: 'bg-summer-dark',
      footerText: 'text-white'
    },
    honestly: {
      header: 'bg-honestly-primary',
      headerText: 'text-white',
      primary: 'bg-honestly-primary',
      primaryHover: 'hover:bg-honestly-secondary',
      primaryText: 'text-honestly-primary',
      primaryBg: 'bg-honestly-dark',
      secondary: 'bg-honestly-tertiary',
      accent: 'bg-honestly-accent',
      footer: 'bg-honestly-primary',
      footerText: 'text-white'
    },
    sweets: {
      header: 'bg-sweets-primary',
      headerText: 'text-white',
      primary: 'bg-sweets-primary',
      primaryHover: 'hover:bg-sweets-secondary',
      primaryText: 'text-sweets-primary',
      primaryBg: 'bg-sweets-dark',
      secondary: 'bg-sweets-tertiary',
      accent: 'bg-sweets-accent',
      footer: 'bg-sweets-secondary',
      footerText: 'text-white'
    },
    fabled: {
      header: 'bg-fabled-primary',
      headerText: 'text-white',
      primary: 'bg-fabled-primary',
      primaryHover: 'hover:bg-fabled-secondary',
      primaryText: 'text-fabled-primary',
      primaryBg: 'bg-fabled-tertiary',
      secondary: 'bg-fabled-accent',
      accent: 'bg-fabled-dark',
      footer: 'bg-fabled-accent',
      footerText: 'text-white'
    }
  };

  const currentColors = colorSchemes[settings.colorScheme] || colorSchemes.craft;

  // Helper function to get background styles based on position setting
  const getBackgroundStyles = () => {
    // Check for explicit background color (from page settings)
    if (settings.backgroundColor && !settings.backgroundImage) {
      return {
        backgroundColor: settings.backgroundColor
      };
    }

    if (!settings.backgroundImage) {
      // Plain white background when no image is set
      return {
        backgroundColor: '#ffffff'
      };
    }

    // When background image is set, apply positioning
    const baseStyles = {
      backgroundImage: `url(${settings.backgroundImage})`,
      backgroundColor: '#e0f2fe'
    };

    const size = settings.backgroundSize || 100;

    switch (settings.backgroundPosition) {
      case 'stretch':
        return {
          ...baseStyles,
          backgroundSize: '100% 100%',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center'
        };
      case 'repeat':
        return {
          ...baseStyles,
          backgroundRepeat: 'repeat',
          backgroundSize: `${size}%`
        };
      case 'fit':
      default:
        return {
          ...baseStyles,
          backgroundSize: size === 100 ? 'cover' : `${size}%`,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        };
    }
  };

  return (
    <div
      className="min-h-screen relative"
      style={{
        backgroundColor: settings.backgroundColor && !settings.backgroundImage ? settings.backgroundColor : '#ffffff'
      }}
    >
      {/* Background Image Layer with Opacity */}
      {settings.backgroundImage && (
        <div
          className="fixed inset-0"
          style={{
            backgroundImage: `url(${settings.backgroundImage})`,
            backgroundRepeat: settings.backgroundPosition === 'repeat' ? 'repeat' : 'no-repeat',
            backgroundSize: (() => {
              const size = settings.backgroundSize || 100;
              if (settings.backgroundPosition === 'stretch') return '100% 100%';
              if (settings.backgroundPosition === 'repeat') return `${size}%`;
              return size === 100 ? 'cover' : `${size}%`;
            })(),
            backgroundPosition: settings.backgroundPosition === 'stretch' || settings.backgroundPosition === 'repeat' ? 'initial' : 'center',
            backgroundAttachment: settings.backgroundPosition === 'fit' || !settings.backgroundPosition ? 'fixed' : 'initial',
            opacity: settings.backgroundOpacity !== undefined ? settings.backgroundOpacity / 100 : 1,
            zIndex: 0,
            pointerEvents: 'none'
          }}
        />
      )}

      {/* Content Layer */}
      <div className="relative" style={{ zIndex: 1 }}>
      {/* Header - Customizable background and height */}
      {showHeader && (
        <header
          className="shadow-md border-b border-gray-200 relative z-10"
          style={{ backgroundColor: settings.headerBackgroundColor || '#ffffff' }}
        >
          <div
            className="px-4"
            style={{
              paddingTop: settings.headerHeight === 'small' ? '12px' : settings.headerHeight === 'large' ? '32px' : '24px',
              paddingBottom: settings.headerHeight === 'small' ? '12px' : settings.headerHeight === 'large' ? '32px' : '24px'
            }}
          >
            {/* Header Content */}
            {settings.headerLayout === 'centered' ? (
              /* Centered Layout */
              <div className="mb-4 relative">
                {/* Social Icons - Top Right */}
                <div className="absolute top-0 right-0 flex items-center gap-2">
                  <div className="flex items-center gap-2">
                    <a
                      href="https://facebook.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-500 hover:text-pink-500 transition-colors"
                      aria-label="Facebook"
                    >
                      <Facebook className="w-4 h-4 sm:w-5 sm:h-5" />
                    </a>
                    <a
                      href="https://instagram.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-500 hover:text-pink-500 transition-colors"
                      aria-label="Instagram"
                    >
                      <Instagram className="w-4 h-4 sm:w-5 sm:h-5" />
                    </a>
                  </div>
                </div>

                {/* Centered Logo */}
                <div className="flex flex-col items-center">
                  <div className="mb-4">
                    {settings.logo ? (
                      <img
                        src={settings.logo}
                        alt="Logo"
                        className="object-contain"
                        style={{
                          maxHeight: settings.logoSize === 'xsmall' ? '60px' : settings.logoSize === 'small' ? '80px' : settings.logoSize === 'large' ? '120px' : settings.logoSize === 'xlarge' ? '140px' : '100px',
                          height: 'auto',
                          width: 'auto'
                        }}
                      />
                    ) : (
                      <div className="h-20 w-20 bg-gray-100 rounded-lg flex items-center justify-center">
                        <span className="text-3xl font-bold text-gray-600">
                          {(settings.businessName || teacherInfo?.first_name || 'C').charAt(0)}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Centered Navigation */}
                  <nav>
                    <ul className="flex flex-wrap justify-center gap-6 text-sm uppercase tracking-wide" style={{ fontFamily: settings.navFont && settings.navFont !== 'system' ? `'${settings.navFont}', sans-serif` : 'inherit' }}>
                      {pages.filter(page => page.show_in_navigation).map((page) => (
                        <li key={page.id}>
                          <a
                            href={page.is_home_page ? `/portal/${userId}` : `/portal/${userId}/${page.slug}`}
                            className="transition-colors font-semibold"
                            style={{
                              color: currentPage === page.slug || (currentPage === null && page.is_home_page)
                                ? (settings.navActiveIndicatorColor || '#3b82f6')
                                : (settings.navFontColor || '#4b5563'),
                              borderBottom: currentPage === page.slug || (currentPage === null && page.is_home_page)
                                ? `2px solid ${settings.navActiveIndicatorColor || '#3b82f6'}`
                                : '2px solid transparent',
                              paddingBottom: '4px'
                            }}
                            onMouseEnter={(e) => {
                              if (!(currentPage === page.slug || (currentPage === null && page.is_home_page))) {
                                e.currentTarget.style.color = settings.navActiveIndicatorColor || '#3b82f6';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!(currentPage === page.slug || (currentPage === null && page.is_home_page))) {
                                e.currentTarget.style.color = settings.navFontColor || '#4b5563';
                              }
                            }}
                          >
                            {page.title}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </nav>
                </div>
              </div>
            ) : (
              /* Standard Layout */
              <div className="flex items-end justify-between mb-4 relative">
                {/* Logo - Left Side */}
                <div style={{
                  transform: 'translateY(20px)',
                  marginLeft: `${settings.logoIndent || 0}px`
                }}>
                  {settings.logo ? (
                    <img
                      src={settings.logo}
                      alt="Logo"
                      className="object-contain"
                      style={{
                        maxHeight: settings.logoSize === 'xsmall' ? '60px' : settings.logoSize === 'small' ? '80px' : settings.logoSize === 'large' ? '120px' : settings.logoSize === 'xlarge' ? '140px' : '100px',
                        height: 'auto',
                        width: 'auto'
                      }}
                    />
                  ) : (
                    <div className="h-20 w-20 bg-gray-100 rounded-lg flex items-center justify-center">
                      <span className="text-3xl font-bold text-gray-600">
                        {(settings.businessName || teacherInfo?.first_name || 'C').charAt(0)}
                      </span>
                    </div>
                  )}
                </div>

                {/* Navigation Menu - Center */}
                <nav className="flex-1 flex justify-center">
                  <ul className="flex flex-wrap gap-6 text-sm uppercase tracking-wide" style={{ fontFamily: settings.navFont && settings.navFont !== 'system' ? `'${settings.navFont}', sans-serif` : 'inherit' }}>
                    {pages.filter(page => page.show_in_navigation).map((page) => (
                      <li key={page.id}>
                        <a
                          href={page.is_home_page ? `/portal/${userId}` : `/portal/${userId}/${page.slug}`}
                          className="transition-colors font-semibold"
                          style={{
                            color: currentPage === page.slug || (currentPage === null && page.is_home_page)
                              ? (settings.navActiveIndicatorColor || '#3b82f6')
                              : (settings.navFontColor || '#4b5563'),
                            borderBottom: currentPage === page.slug || (currentPage === null && page.is_home_page)
                              ? `2px solid ${settings.navActiveIndicatorColor || '#3b82f6'}`
                              : '2px solid transparent',
                            paddingBottom: '4px'
                          }}
                          onMouseEnter={(e) => {
                            if (!(currentPage === page.slug || (currentPage === null && page.is_home_page))) {
                              e.currentTarget.style.color = settings.navActiveIndicatorColor || '#3b82f6';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!(currentPage === page.slug || (currentPage === null && page.is_home_page))) {
                              e.currentTarget.style.color = settings.navFontColor || '#4b5563';
                            }
                          }}
                        >
                          {page.title}
                        </a>
                      </li>
                    ))}
                  </ul>
                </nav>

                {/* Contact Button and Social Icons - Right Side */}
                {settings.contactEmail && (
                  <div className="flex items-center gap-2 sm:gap-3">
                  <a
                    href={`mailto:${settings.contactEmail}`}
                    className="text-xs text-gray-500 hover:text-pink-500 border border-gray-300 px-2 sm:px-3 py-1 rounded transition-colors whitespace-nowrap"
                  >
                    <span className="hidden sm:inline">Join My Mailing List</span>
                    <span className="sm:hidden">Join List</span>
                  </a>

                  {/* Social Media Icons */}
                  <div className="flex items-center gap-2">
                    <a
                      href="https://facebook.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-500 hover:text-pink-500 transition-colors"
                      aria-label="Facebook"
                    >
                      <Facebook className="w-4 h-4 sm:w-5 sm:h-5" />
                    </a>
                    <a
                      href="https://instagram.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-500 hover:text-pink-500 transition-colors"
                      aria-label="Instagram"
                    >
                      <Instagram className="w-4 h-4 sm:w-5 sm:h-5" />
                    </a>
                  </div>
                  </div>
                )}
              </div>
            )}

            {/* Divider Line */}
            <div className="border-t border-gray-200"></div>
          </div>
        </header>
      )}

      {/* Main Content */}
      <main className="flex-1">
        {children}
      </main>

      {/* Footer */}
      {showFooter && (
        <footer className={`${currentColors.footer} ${currentColors.footerText} py-8 mt-12`}>
          <div className="px-4">
            <div className="grid md:grid-cols-3 gap-8">
              {/* Contact Information */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Contact Information</h3>
                {settings.contactEmail && (
                  <p className="text-gray-300 mb-2">
                    <span className="font-medium">Email:</span> {settings.contactEmail}
                  </p>
                )}
                {settings.contactPhone && (
                  <p className="text-gray-300 mb-2">
                    <span className="font-medium">Phone:</span> {settings.contactPhone}
                  </p>
                )}
                {settings.address && (
                  <p className="text-gray-300">
                    <span className="font-medium">Address:</span> {settings.address}
                  </p>
                )}
              </div>

              {/* Quick Links */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
                <ul className="space-y-2 text-gray-300">
                  {pages.filter(page => page.show_in_navigation).slice(0, 5).map((page) => (
                    <li key={page.id}>
                      <a
                        href={page.is_home_page ? `/portal/${userId}` : `/portal/${userId}/${page.slug}`}
                        className="hover:text-white transition-colors"
                      >
                        {page.title}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Powered By */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Powered By</h3>
                <p className="text-gray-300">
                  <a 
                    href="https://classtraks.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="hover:text-white transition-colors"
                  >
                    ClassTraks
                  </a>
                </p>
                <p className="text-gray-400 text-sm mt-2">
                  Simplifying class management
                </p>
              </div>
            </div>
            
            <div className="border-t border-gray-700 mt-8 pt-4 text-center text-gray-400 text-sm">
              <p>&copy; 2025 {settings.businessName || `${teacherInfo?.first_name || ''} ${teacherInfo?.last_name || ''}`}. All rights reserved.</p>
            </div>
          </div>
        </footer>
      )}
      </div>
      {/* End Content Layer */}
    </div>
  );
};

export default PortalLayout;